import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaInstagram, FaWhatsapp, FaVk, FaEnvelope } from 'react-icons/fa';
import { useTheme } from './hooks/useTheme';

const GalleryModal = React.lazy(() => import('./GalleryModal'));
const NeonCanvas = React.lazy(() => import('./NeonCanvas'));

const slogans = [
  "Ощути твое идеальное звучание", // Русский
  "Идеаль тавышыңны той", // Татарский
  "Feel your perfect sound", // Английский
  "Ressentez votre son parfait", // Французский
  "당신의 완벽한 소리를 느껴보세요", // Корейский
  "Siente tu sonido perfecto", // Испанский
  "完璧な音を感じて", // Японский
  "Senti il tuo suono perfetto", // Итальянский
  "สัมผัสเสียงที่สมบูรณ์แบบของคุณ", // Тайский
  "Osjetite svoj savršeni zvuk" // Хорватский
];

export default function Home() {
  const { theme, toggleTheme } = useTheme();
  const [sloganIndex, setSloganIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const memoizedNeonCanvas = useMemo(() => (
    <Suspense fallback={null}>
      <NeonCanvas theme={theme} />
    </Suspense>
  ), [theme]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSloganIndex(prev => (prev + 1) % slogans.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-white dark:bg-gray-900">
      {memoizedNeonCanvas}

      <motion.div 
        className="relative z-10 text-center px-4 w-full flex flex-col items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.h1 
          className="text-5xl md:text-7xl font-bold mb-6 text-white dark:text-white drop-shadow-[0_0_10px_rgba(255,0,255,0.7)]"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Lux Aeterna
        </motion.h1>
        
        <motion.p 
          className="text-xl md:text-2xl mb-8 text-gray-300 dark:text-gray-300 min-h-[60px] flex items-center justify-center"
          key={sloganIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {slogans[sloganIndex]}
        </motion.p>

        <div className="flex space-x-6 mb-10 justify-center">
          <motion.a 
            href="https://www.instagram.com/lux_choir/" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-500 hover:text-pink-400 text-3xl"
            whileHover={{ y: -5, scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaInstagram />
          </motion.a>
          <motion.a 
            href="https://wa.me/79196939976" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-500 hover:text-green-400 text-3xl"
            whileHover={{ y: -5, scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaWhatsapp />
          </motion.a>
          <motion.a 
            href="https://vk.com/club225109897" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-400 text-3xl"
            whileHover={{ y: -5, scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaVk />
          </motion.a>
        </div>

        <motion.button
          onClick={() => setIsGalleryOpen(true)}
          className="px-8 py-3 bg-transparent border-2 border-pink-500 text-white dark:text-white font-bold rounded-lg relative overflow-hidden group hover:border-pink-600 hover:text-pink-500 dark:hover:text-pink-500 transition-all duration-300"
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(255, 0, 255, 0.6)' }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="absolute inset-0 bg-pink-500 opacity-10 group-hover:opacity-20 transition-all duration-300"></span>
          <span className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-10 transition-all duration-300"></span>
          <div className="relative flex items-center justify-center space-x-2">
            <span className="text-lg">Открыть галерею</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        </motion.button>
      </motion.div>

      {/* Ссылка на Telegram автора */}
      <motion.a
        href="mailto:punker02@gmail.com"
        className="fixed bottom-4 left-4 z-20 flex items-center gap-2 px-3 py-1 bg-black/5 dark:bg-white/5 text-gray-500 dark:text-gray-400 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-200 border border-gray-300/20 dark:border-gray-600/20"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
      >
        <FaEnvelope className="text-sm opacity-70" />
        <span className="text-xs font-light opacity-70">Автор</span>
      </motion.a>

      <button 
        onClick={toggleTheme}
        className="absolute bottom-4 right-4 p-2 rounded-full bg-black bg-opacity-50 text-white dark:bg-white dark:bg-opacity-20 dark:text-white z-20"
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? '🌙' : '☀️'}
      </button>

      {isGalleryOpen && (
        <Suspense fallback={<div className="fixed inset-0 bg-black z-50 flex items-center justify-center">Загрузка галереи...</div>}>
          <GalleryModal 
            isOpen={isGalleryOpen} 
            onClose={() => setIsGalleryOpen(false)} 
          />
        </Suspense>
      )}
    </div>
  );
}